/**
 * Candlestick chart builder (ADR-0079 §4.1, §4.4).
 *
 * Time-series OHLC. Each row → one candle. ECharts's candlestick series
 * requires `[open, close, low, high]` order (NOT the spec's natural OHLC
 * order — be careful), so the renderer reorders columns when emitting
 * `series[0].data`.
 */

import type { CandlestickSpec } from "../echartsSpec.js";
import {
  buildAxisLabelFormatter,
  buildNoDataGraphic,
  resolveColumnIndex,
  resolvePalette,
} from "./shared.js";

type EChartsOption = Record<string, unknown>;

export function buildCandlestickOption(
  spec: CandlestickSpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  const xIdx = resolveColumnIndex(spec.encoding.x.column, dimensions);
  const openIdx = resolveColumnIndex(spec.encoding.ohlc.open, dimensions);
  const closeIdx = resolveColumnIndex(spec.encoding.ohlc.close, dimensions);
  const lowIdx = resolveColumnIndex(spec.encoding.ohlc.low, dimensions);
  const highIdx = resolveColumnIndex(spec.encoding.ohlc.high, dimensions);

  const allResolved =
    xIdx !== null && openIdx !== null && closeIdx !== null && lowIdx !== null && highIdx !== null;

  // Categories = list of x-axis values in row order
  const categories: unknown[] = [];
  // Data = [[open, close, low, high], ...] (ECharts convention)
  const data: Array<[number | null, number | null, number | null, number | null]> = [];

  if (allResolved) {
    for (const row of rows) {
      categories.push(row[xIdx as number]);
      data.push([
        coerce(row[openIdx as number]),
        coerce(row[closeIdx as number]),
        coerce(row[lowIdx as number]),
        coerce(row[highIdx as number]),
      ]);
    }
  }

  const xAxis: Record<string, unknown> = {
    type: spec.encoding.x.dataType,
    data: spec.encoding.x.dataType === "category" ? categories : undefined,
    ...(spec.encoding.x.format
      ? { axisLabel: { formatter: buildAxisLabelFormatter(spec.encoding.x.format) } }
      : {}),
  };

  const yAxis: Record<string, unknown> = {
    type: "value",
    scale: true,
    ...(spec.options.yAxis?.min !== undefined ? { min: spec.options.yAxis.min } : {}),
    ...(spec.options.yAxis?.max !== undefined ? { max: spec.options.yAxis.max } : {}),
    ...(spec.options.yAxis?.format
      ? { axisLabel: { formatter: buildAxisLabelFormatter(spec.options.yAxis.format) } }
      : {}),
  };

  const palette = resolvePalette(spec.options.color, 1);

  return {
    ...(spec.options.title
      ? {
          title: {
            text: spec.options.title,
            ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}),
          },
        }
      : {}),
    ...(palette ? { color: palette } : {}),
    legend: { show: spec.options.legend?.show !== false },
    tooltip: {
      show: spec.options.tooltip?.show !== false,
      trigger: "axis",
    },
    xAxis,
    yAxis,
    series: [
      {
        type: "candlestick",
        data,
      },
    ],
    ...(isEmpty ? { graphic: buildNoDataGraphic() } : {}),
  };
}

function coerce(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
